import { A } from '@ember/array';
import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { COMPANY } from 'sh-www/models/company';

/**
 * Error that would come back from the server when a company does not support public projects.
 *
 * @type {string}
 */
const ERROR_COMPANY_DOES_NOT_SUPPORT_PUBLIC_PROJECTS_MESSAGE = 'Company does not support public listing of projects.';

/**
 * Service for company related API calls.
 */
export default Service.extend({
  api: service(),
  errorTracker: service('error-tracker'),
  session: service(),
  store: service(),
  shData: service(),
  appCache: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * @param {User} seller
   * @param {Application=} application
   * @return {Promise<*>}
   */
  ccCaptureDeclined(seller, application) {
    const jsonData = { };

    if (seller) {
      jsonData.seller_id = seller.id;
    }

    if (application) {
      jsonData.application_id = application.id;
    }

    return this.api.post(`companies/${this.session.currentUser.company.id}/cards/capture-declined`, {
      jsonData,
    });
  },

  /**
   * Attaches an asset to a company.
   *
   * @param {Company} company
   * @param {Asset} asset
   * @returns {Promise}
   */
  attachAssetToCompany(company, asset) {
    const assetID = asset.data.id;
    const endpoint = `/companies/${company.get('id')}/assets/${assetID}/attach`;
    const options = {};

    return this.get('api').post(endpoint, options)
      .then(() => {
        this.shData.pushResponseToStore(asset);
        const assetModel = this.get('store').peekRecord('asset', assetID);
        company.get('assets').addObject(assetModel);
      });
  },

  createLegal(companyId, title, content) {
    const options = {
      jsonData: {
        title,
        content
      }
    };

    return this.api.post(`companies/${companyId}/legals`, options);
  },

  /**
   * Create a new company from the parameters passed in.
   *
   * @param {Object} company
   * @param {string} company.name
   * @param {string} company.description
   * @param {Object=} company.opt_imageData
   * @param {string=} company.opt_stripeToken
   * @param {string=} company.opt_locationId
   * @param {string=} company.opt_domain
   * @param {string=} company.opt_website
   * @param {string=} company.opt_companyGroupId
   * @param {boolean=} company.opt_isSharingRates    False by default and only respected if opt_companyGroupId is set
   * @param {string=} company.opt_type
   * @return {Promise<*>}
   */
  async createNewCompany({
    name,
    description,
    opt_imageData = undefined,
    opt_stripeToken = undefined,
    opt_locationId = undefined,
    opt_domain = '',
    opt_website = '',
    opt_companyGroupId = undefined,
    opt_isSharingRates = false,
    opt_type = undefined,
  }) {
    const options = {
      jsonData: {
        description: description,
        name: name,
      },
    };

    if (opt_imageData) {
      options.jsonData['image_cloud_id'] = opt_imageData.public_id;
      options.jsonData['image_cloud_version'] = opt_imageData.version;
    }

    if (opt_stripeToken) {
      options.jsonData['stripe_token'] = opt_stripeToken;
    }

    if (opt_companyGroupId) {
      // if we have a group id, we should check the sharing rates flag.
      options.jsonData['group_id'] = opt_companyGroupId;
      options.jsonData['company_group_shares_rates'] = opt_isSharingRates;
    }

    if (opt_domain) {
      options.jsonData['domain'] = opt_domain;
    }

    if (opt_locationId) {
      options.jsonData['location_id'] = opt_locationId;
    }

    if (opt_website) {
      options.jsonData['website'] = opt_website;
    }

    if (opt_type) {
      options.jsonData['type'] = opt_type;
    }

    const response = await this.api.post('companies/', options);
    this.store.pushPayload(response);
    return this.store.peekRecord('company', response.data.id);
  },

  updateCompany(companyId, opt_name = null, opt_imageData = null, opt_website = null, opt_locationId = null) {
    const options = { jsonData: {} };

    if (opt_name) {
      options.jsonData['name'] = opt_name;
    }

    if (opt_imageData) {
      options.jsonData['image_cloud_id'] = opt_imageData.public_id;
      options.jsonData['image_cloud_version'] = opt_imageData.version;
    }

    if (opt_website) {
      options.jsonData['website'] = opt_website;
    }

    if (opt_locationId) {
      options.jsonData['location_id'] = opt_locationId;
    }

    return this.api.put(`/companies/${companyId}`, options).then((response) => {
      this.store.pushPayload(response);
      return this.store.peekRecord('company', response.data.id);
    });
  },

  /**
   * Delete an asset from the company and all applications it was used in.
   *
   * @param {Company} company
   * @param {Asset} asset
   * @param {String[]} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  deleteAsset(company, asset, opt_includes = []) {
    return new Promise((resolve, reject) => {
      let options = {};
      this.shData.addIncludesToOptions(options, opt_includes);

      this.get('api').post(`/companies/${company.get('id')}/assets/${asset.get('id')}/detach`, options).then(response => {

        // update the assets for the company.
        let updatedAssets = company.get('assets').filter(assetToFilter => {
          return assetToFilter.get('id') !== asset.get('id');
        });

        company.set('assets', updatedAssets);

        // remove all of these assets from any apps loaded in memory
        this.get('store').peekAll('application').forEach(application => {
          let updatedAssets = application.get('assets').filter(assetToFilter => {
            return assetToFilter.get('id') !== asset.get('id');
          });

          application.set('assets', updatedAssets);
        });

        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error deleting your asset. Please refresh and try again.',
          'deleteAsset -- error removing asset id: ' + asset.get('id') + ' and company id: ' + company.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Load block user events for a seller
   *
   * @param {User} seller
   * @param {String=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadBlockUserEventsForSeller(seller, opt_includes) {
    const myCompany = this.currentUser?.company;

    // There are no events if we don't have a company
    if (!myCompany) {
      return Promise.resolve(true);
    }

    const company = this.store.peekRecord('company', myCompany.id),
      options = {
        params : {
          filter : {
            user_id : seller.id
          }
        }
      };

    if (opt_includes) {
      options.params['include'] = opt_includes;
    }

    return this.api.request(
      `companies/${company.id}/block-user-events`,
      options
    ).then(response => {
      this.shData.pushResponseToStore(response);
      return true;
    }).catch(e => {
      this.get('errorTracker').error(`loadBlockUserEvents -- error getting blocked user events ${company.get('id')}`, this, e);
      throw e;
    });
  },

  /**
   * Given a company and a stripeToken, save a new card for the user's company.
   *
   * @param {Company} company
   * @param {String} stripeToken
   * @return {Ember.RSVP.Promise}   Resolve with a Card on success
   */
  addNewCardForCompany(company, stripeToken) {
    let companyId = company.get('id'),
      options = {
        jsonData: {
          stripeToken: stripeToken
        }
      };

    return new Promise((resolve, reject) => {
      this.get('api').post(`companies/${companyId}/cards`, options).then((response) => {
        this.get('store').pushPayload(response);

        let card = this.get('store').peekRecord('card', response.data.id);
        company.get('cards').addObject(card);

        resolve(card);
      }).catch(e => {
        this.get('errorTracker').error(`addNewCardForCompany -- error adding card company ${companyId} and user id ${this.get('currentUser.id')}`, this, e);
        reject(e);
      });
    });
  },

  /**
   * Delete a card for a particular company.
   *
   * @param {Company} company
   * @param {Card} card
   * @return {Ember.RSVP.Promise}
   */
  deleteCardForCompany(company, card) {
    let options = {};

    return new Promise((resolve, reject) => {
      this.get('api').delete(`companies/${company.get('id')}/cards/${card.get('id')}`, options).then(() => {
        company.get('cards').removeObject(card);
        card.deleteRecord();

        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error(`deleteCardForCompany -- error deleting card company ${company.get('id')} and user id ${this.get('currentUser.id')}`, this, e);
        reject(e);
      });
    });
  },

  /**
   *
   * @param companyId
   * @returns {*}
   */
  loadInvites(companyId, page = 1, count = 15, filters = {}) {
    const options = {
      params: {
        page,
        count,
        filter: { ...filters },
      },
    };

    return this.api.request(`companies/${companyId}/invites/`, options).then(response => {
      this.shData.pushResponseToStore(response);

      const responseObject = {
        invites: this.shData.getModelArrayFromResponse(response, 'invite'),
        pagination: this.shData.getPaginationModelFromResponse(response)
      };

      return responseObject;
    });
  },

  loadOutstandingInvoices(delinquentOnly = false) {
    const filter = {
      company_id: this.session.currentUser.company.id,
      paid: 0,
      is_child: 0,
    };

    if (delinquentOnly) {
      filter['delinquent'] = 1;
    } else {
      filter['overdue'] = 1;
    }

    return this.api.get('sh-invoices', {
      params: {
        filter,
        sort: 'final_at',
        order: 'asc',
        unpaged: 1,
      },
    }).then(response => {
      this.store.pushPayload(response);

      return response.data.map(
        ({ id }) => this.store.peekRecord('sh-invoice', id),
      );
    });
  },

  /**
   * Invite a user by email to a specific company
   *
   * @param {Company} company
   * @param {Object} jsonData
   * @param {string} jsonData.email
   * @param {string=} jsonData.first_name
   * @param {string=} jsonData.last_name
   * @param {boolean=} jsonData.is_admin
   * @param {string[]=} opt_includes
   * @param {boolean=} opt_isHandleError
   * @return {Ember.RSVP.Promise}
   */
  inviteUserToCompany(company, jsonData, opt_includes = [], opt_isHandleError = true) {
    const options = {
      jsonData
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(
        `companies/${company.get('id')}/invites/`,
        options
      ).then(response => {
        this.shData.pushResponseToStore(response);
        let invite = this.get('store').peekRecord('invite', response.data.id);
        resolve(invite);
      }).catch(e => {
        if (opt_isHandleError) {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error inviting this email address. Please refresh and try again.',
            'inviteUserToCompany -- error inviting email to company for company id : ' + company.get('id'),
          );
        }

        reject(e);
      });
    });
  },

  /**
   * Load the blocked users for a company.  Will hit the cached version if already loaded in the object.
   *
   * @param {String} companyId
   * @param {Boolean=} opt_skipCache    Set to true to force a refresh.
   * @param {String[]=} opt_includes
   */
  loadBlockedUsersForCompany(companyId, opt_skipCache, opt_includes = []) {
    if (this.get('currentUser.isSeller')) {
      return Promise.resolve(A());
    }

    let company = this.get('store').peekRecord('company', companyId);
    if (!company) {
      this.get('errorTracker').error(`loadBlockedUsersForCompany -- company not found in cache ${companyId}`, this);
      return Promise.reject('Company not found');
    }

    if (!opt_skipCache) {
      let blockedUsers = this.appCache.getCachedModelArrayForRequest(this, 'loadBlockedUsersForCompany', arguments);
      if (blockedUsers) {
        return Promise.resolve(blockedUsers);
      }
    }

    let options = {};
    this.shData.addIncludesToOptions(options, opt_includes);

    return this.get('api').request(`companies/${companyId}/blocked-users`, options).then((response) => {
      this.get('store').pushPayload(response);

      let blockedUsers = company.get('blockedUsers');

      response.data.forEach(userDataJson => {
        let user = this.get('store').peekRecord('user', userDataJson.id);
        blockedUsers.addObject(user);
      });

      this.appCache.setCachedModelArrayForRequest(
        this,
        'loadBlockedUsersForCompany',
        arguments,
        blockedUsers);

      return blockedUsers;
    }).catch(e => {
      this.get('errorTracker').error(`loadBlockedUsersForCompany -- error getting blocked users ${companyId}`, this, e);
      throw e;
    });
  },

  /**
   * Load a company by the id
   *
   * @param {String} companyId
   * @param {Array=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadCompany(companyId, opt_includes = []) {
    let options = this.shData.addIncludesToOptions({}, opt_includes);

    return this.api.request(`companies/${companyId}/`, options).then(response => {
      this.shData.pushResponseToStore(response);

      return this.get('store').peekRecord('company', response.data.id);
    });
  },

  loadCompanySellerConnection(companyId, sellerId, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);
    return this.api.request(`companies/${companyId}/freelancer-connections/${sellerId}`, options);
  },

  /**
   * Load a company's legals
   *
   * @param {String} companyId
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyLegals(companyId) {
    return this.api.request(`companies/${companyId}/legals`, {});
  },

  /**
   * Load a companies project digests
   *
   * @param {String} companySlug
   * @param {String[]} opt_includes
   * @param {Number=} opt_count
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyProjectDigests(companySlug, opt_includes = [], opt_count = 10) {
    const options = {
      params : {
        count: opt_count
      }
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.get('api').request(`companies/${companySlug}/projectdigests`, options).then(response => {
      this.shData.pushResponseToStore(response);
      const digests = this.shData.getModelArrayFromResponse(response, 'projectdigest');
      return digests;
    }).catch(e => {
      const errors = e.errors || null;

      if (errors && errors['company_id']) {
        if (errors['company_id'].includes(ERROR_COMPANY_DOES_NOT_SUPPORT_PUBLIC_PROJECTS_MESSAGE)) {
          // this is a normal case when a company does not allow public viewing of projects, just log and continue.
          return A();
        }
      }

      handleErrorResponseWithModal(
        this,
        e,
        'There was an error loading the company projects. Please refresh and try again.',
        'loadCompanyProjectDigests -- error loading digests for company : ' + companySlug,
      );

      throw e;
    });
  },

  loadIndustries() {
    return this.api.request(`industries/`).then(response => {
      this.shData.pushResponseToStore(response);
      const industries = this.shData.getModelArrayFromResponse(response, 'industry');
      return industries;
    }).catch(e => {
      this.errorTracker.error('loadIndustries -- error loading industries', this, e);
      throw e;
    });
  },

  /**
   * Make a card default for the company.  Also, update all the other company cards and make them
   * NOT default in the client.
   *
   * @param {Card} card
   * @return {Ember.RSVP.Promise}
   */
  makeCardDefault(card) {
    if (card.get('isDefault')) {
      return Promise.resolve(true);
    }

    let companyId = card.get('company.id'),
      options = {
        jsonData: {
          is_default: 1
        }
      };

    return new Promise((resolve, reject) => {
      this.get('api').post(`companies/${companyId}/cards/${card.get('id')}/set-default`, options).then(() => {
        this._updatedDefaultCards(card.get('company'), card);
        resolve(card);
      }).catch(e => {
        this.get('errorTracker').error(`addNewCardForCompany -- error adding card company ${companyId}`, this, e);
        reject(e);
      });
    });
  },

  /**
   * Request an upgrade from a user to a new plan based on the tier.
   *
   * @param {Number} tier
   * @param {String} type       of type PRICING_TABLE_DATA.UPGRADE_REQUEST_*
   * @return {Ember.RSVP.Promise}
   */
   requestUpgrade(tier, type) {
    const options = {
      jsonData: {
        type: type,
        'target-tier': tier
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return this.api.post(`contact/upgrade-request`, options).then(() => {
      return true;
    }).catch(e => {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error requesting an upgrade. Please refresh and try again.',
        'requestUpgrade -- error requesting upgrade for user id : ' + this.currentUser.id,
      );
      throw e;
    });
  },

  /**
   *
   * @param invite
   * @returns {*}
   */
  resendInvite(invite) {
    return this.api.post(`invites/${invite.id}/resend`);
  },

  /**
   * Invite a creator (seller) by email to a company
   *
   * @param Company company
   * @param {Array} invites
   * @param {string} invites[].email
   * @param {string} invites[].user_id
   * @param {string=} invites[].first_name
   * @param {string=} invites[].last_name
   * @return {Ember.RSVP.Promise}
   */
  inviteSellersToCompany(company, invites) {
    const options = {
      jsonData: {
        invitees: JSON.stringify(invites),
      },
    };

    return this.api.post(`companies/${company.id}/freelancer-invites`, options);
  },

  /**
   * Invite override request for a creator (seller) to be added to a company's network
   *
   * @param {String} companyId
   * @param {String} userId
   * @param {string} reason
   * @return {Ember.RSVP.Promise}
   */
  inviteSellerToCompanyRequest(companyId, userId, reason) {
    const options = {
      params: {
        'user_id': userId,
        reason,
      }
    }
    return this.api.post(`companies/${companyId}/freelancer-invite-request`, options);
  },

  /**
   * Cancel an invite
   *
   * @param {Invite} invite
   * @return {Ember.RSVP.Promise}
   */
   cancelInvite(invite) {
    const options = {};

    return this.api.delete(`invites/${invite.id}`, options);
  },

  setDefaultLegal(legalId) {
    return this.api.post(`legals/${legalId}/set-default`, {});
  },

  /**
   * Update the default cards for our company based on the default card passed in.
   *
   * @param {Company} company
   * @param {Card=} opt_card    This will be the card set to default.  If this is NULL, we will pick the first
   *                            card in the list as the default.
   * @private
   */
  _updatedDefaultCards(company, opt_card) {
    let card = opt_card ? opt_card : company.get('cards').objectAt(0);
    if (!card) {
      this.get('errorTracker').error('_updatedDefaultCards -- no card found.' + company.get('id'), this);
      throw new Error('No Default Card To Set');
    }

    card.set('isDefault', true);

    company.get('cards').forEach(companyCard => {
      if (companyCard.get('id') === card.get('id')) {
        return;
      }

      companyCard.set('isDefault', false);
    });
  },

  addDomain(company, domainName) {
    const companyId = company.id;
    const options = {
      jsonData: {
        domainName,
      }
    };

    return this.api.post(`companies/${companyId}/add-domain`, options);
  },

  updateBillingSettings(company, jsonData) {
    return this.api.put(`companies/${company.id}/settings`, {
      jsonData,
    });
  },

  defaultFees() {
    const store = this.store;
    return this.api.request('company-billing-defaults', {}).then((tierVersionToBillingDefaultsMap) => {
      return Object.values(COMPANY.TIER_VERSIONS).reduce((accum, tierVersion) => {
        accum[tierVersion] = tierVersionToBillingDefaultsMap[tierVersion].reduce((accum2, record) => {
          accum2[record.tier] = store.createRecord('company-billing-default', {
            tier: record.tier,
            tierVersion: record.tier_version,
            finderFee: record.fees.finder_fee,
            inviteeFee: record.fees.invitee_fee,
            delinquentFee: record.fees.delinquent_fee,
            expenseFee: record.fees.expense_fee,
            advanceFee: record.fees.advance_fee,
            cancelFee: record.fees.cancel_fee,
            selectBaseFee: record.fees.select_base_fee,
            selectFee: record.fees.select_fee,
            features: record.features,
            invoiceNet: record.invoice_net,
          });

          return accum2;
        }, {});

        return accum;
      }, {});
    }).catch(e => {
      this.errorTracker.error(`defaultFees -- error fetching default fees`, this, e);
      throw e;
    });
  },
});
